import {
  DeleteOutlined,
  ExpandOutlined,
  PlusOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Empty,
  Flex,
  Image,
  List,
  Modal,
  Select,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useToken } from "../../hooks/useToken";
// --- ALTERADO: Importa o novo modal adaptado ---
import dayjs from "dayjs";
import { TbArrowsDiagonalMinimize2 } from "react-icons/tb";
import { ModalPostNew } from "../timeline/ModalPostNew";

// ... (resto das definições de tipo, constantes, fetchData, SettingsModal - sem alterações) ...
const API_BASE_URL = "https://www.thesportsdb.com/api/v1/json/";
const API_KEY = "3";

export type TCountry = { name_en: string };
export type TLeague = { idLeague: string; strLeague: string; strSport: string };
export type TTeam = { idTeam: string; strTeam: string; strTeamBadge: string };
export type TFavoriteTeam = {
  teamId: string;
  teamName: string;
  sport: string;
  leagueId: string;
  leagueName: string;
  teamBadge?: string;
};
export type TEvent = {
  idEvent: string;
  strEvent: string;
  strLeague: string;
  strSport: string;
  dateEvent: string;
  strTime: string;
  strHomeTeam: string;
  strAwayTeam: string;
  intHomeScore: string | null;
  intAwayScore: string | null;
  strStatus?: string;
  idHomeTeam: string;
  idAwayTeam: string;
  strHomeTeamBadge?: string;
  strAwayTeamBadge?: string;
};

async function fetchData<T>(url: string): Promise<T | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    const mainKey = Object.keys(data)[0];
    if (!data || data[mainKey] === null) return null;
    return data as T;
  } catch (error) {
    console.error("Error fetching data:", error);
    message.error(`Failed to fetch data from ${url}. See console for details.`);
    return null;
  }
}

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
  initialTeams: TFavoriteTeam[];
  onSave: (teams: TFavoriteTeam[]) => void;
}

function SettingsModal({
  open,
  onClose,
  initialTeams,
  onSave,
}: SettingsModalProps) {
  // ... (código do SettingsModal inalterado) ...
  const { token } = useToken();
  const [configuredTeams, setConfiguredTeams] =
    useState<TFavoriteTeam[]>(initialTeams);

  const [countries, setCountries] = useState<TCountry[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string | undefined>(
    undefined
  );
  const [loadingCountries, setLoadingCountries] = useState(false);

  const [sports] = useState<string[]>([
    "Soccer",
    "Basketball",
    "Baseball",
    "Tennis",
    "Golf",
    "Fighting",
    "Motorsport",
    "American Football",
    "Ice Hockey",
    "Cricket",
  ]);
  const [selectedSport, setSelectedSport] = useState<string | undefined>(
    undefined
  );

  const [leagues, setLeagues] = useState<TLeague[]>([]);
  const [selectedLeague, setSelectedLeague] = useState<string | undefined>(
    undefined
  );
  const [loadingLeagues, setLoadingLeagues] = useState(false);

  const [teams, setTeams] = useState<TTeam[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string | undefined>(
    undefined
  );
  const [loadingTeams, setLoadingTeams] = useState(false);

  useEffect(() => {
    if (open) {
      setLoadingCountries(true);
      fetchData<{ countries: TCountry[] | null }>(
        `${API_BASE_URL}/${API_KEY}/all_countries.php`
      )
        .then((data) => setCountries(data?.countries ?? []))
        .finally(() => setLoadingCountries(false));
    } else {
      setCountries([]);
      setSelectedCountry(undefined);
      setSelectedSport(undefined);
      setLeagues([]);
      setSelectedLeague(undefined);
      setTeams([]);
      setSelectedTeam(undefined);
    }
  }, [open]);

  useEffect(() => {
    if (selectedCountry && selectedSport) {
      setLoadingLeagues(true);
      setLeagues([]);
      setSelectedLeague(undefined);
      setTeams([]);
      setSelectedTeam(undefined);
      fetchData<{ leagues: TLeague[] | null; countries: TLeague[] | null }>(
        `${API_BASE_URL}/${API_KEY}/search_all_leagues.php?c=${encodeURIComponent(
          selectedCountry
        )}&s=${encodeURIComponent(selectedSport)}`
      )
        .then((data) => setLeagues(data?.leagues ?? data?.countries ?? []))
        .finally(() => setLoadingLeagues(false));
    } else {
      setLeagues([]);
    }
  }, [selectedCountry, selectedSport]);

  useEffect(() => {
    if (selectedLeague) {
      const selectedLeagueName = leagues.find(
        (l) => l.idLeague === selectedLeague
      )?.strLeague;
      if (!selectedLeagueName) return;

      setLoadingTeams(true);
      setTeams([]);
      setSelectedTeam(undefined);
      fetchData<{ teams: TTeam[] | null }>(
        `${API_BASE_URL}/${API_KEY}/search_all_teams.php?l=${encodeURIComponent(
          selectedLeagueName
        )}`
      )
        .then((data) => setTeams(data?.teams ?? []))
        .finally(() => setLoadingTeams(false));
    } else {
      setTeams([]);
    }
  }, [selectedLeague, leagues]);

  const handleAddTeam = () => {
    const teamToAdd = teams.find((t) => t.idTeam === selectedTeam);
    const leagueToAdd = leagues.find((l) => l.idLeague === selectedLeague);

    if (teamToAdd && leagueToAdd && selectedSport) {
      if (configuredTeams.some((fav) => fav.teamId === teamToAdd.idTeam)) {
        message.warning(`${teamToAdd.strTeam} is already in your favorites.`);
        return;
      }
      const newFavorite: TFavoriteTeam = {
        teamId: teamToAdd.idTeam,
        teamName: teamToAdd.strTeam,
        sport: selectedSport,
        leagueId: leagueToAdd.idLeague,
        leagueName: leagueToAdd.strLeague,
        teamBadge: teamToAdd.strTeamBadge,
      };
      setConfiguredTeams((prev) => [...prev, newFavorite]);
    } else {
      message.error("Please select a country, sport, league, and team to add.");
    }
  };

  const handleRemoveTeam = (teamIdToRemove: string) => {
    setConfiguredTeams((prev) =>
      prev.filter((team) => team.teamId !== teamIdToRemove)
    );
  };

  const handleSave = () => {
    onSave(configuredTeams);
    onClose();
  };

  return (
    <Modal
      title="Sports"
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText="Save"
      cancelText="Cancel"
      width={800}
      maskClosable={false}
    >
      {/* ... (JSX do SettingsModal inalterado) ... */}
      <Flex vertical gap="middle">
        <Card size="small" title="Add a New Favorite Team">
          <Flex gap="small" wrap="wrap">
            <Select
              style={{ minWidth: 150 }}
              placeholder="Select Sport"
              value={selectedSport}
              onChange={setSelectedSport}
              options={sports.map((s) => ({ value: s, label: s }))}
            />
            <Select
              style={{ minWidth: 180 }}
              showSearch
              placeholder="Select Country"
              value={selectedCountry}
              onChange={setSelectedCountry}
              loading={loadingCountries}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={countries.map((c) => ({
                value: c.name_en,
                label: c.name_en,
              }))}
              disabled={loadingCountries || !selectedSport}
            />
            <Select
              style={{ minWidth: 200, flexGrow: 1 }}
              showSearch
              placeholder="Select League"
              value={selectedLeague}
              onChange={setSelectedLeague}
              loading={loadingLeagues}
              disabled={!selectedCountry || !selectedSport || loadingLeagues}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={leagues.map((l) => ({
                value: l.idLeague,
                label: l.strLeague,
              }))}
              notFoundContent={
                loadingLeagues ? (
                  <Spin size="small" />
                ) : selectedCountry && selectedSport ? (
                  "No leagues found"
                ) : (
                  "Select country and sport"
                )
              }
            />
            <Select
              style={{ minWidth: 200, flexGrow: 1 }}
              showSearch
              placeholder="Select Team"
              value={selectedTeam}
              onChange={setSelectedTeam}
              loading={loadingTeams}
              disabled={!selectedLeague || loadingTeams}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={teams.map((t) => ({
                value: t.idTeam,
                label: t.strTeam,
              }))}
              notFoundContent={
                loadingTeams ? (
                  <Spin size="small" />
                ) : selectedLeague ? (
                  "No teams found"
                ) : (
                  "Select league"
                )
              }
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddTeam}
              disabled={
                !selectedTeam ||
                loadingCountries ||
                loadingLeagues ||
                loadingTeams
              }
            >
              Add
            </Button>
          </Flex>
        </Card>
        <Typography.Title level={5}>Your Favorites</Typography.Title>
        {configuredTeams.length === 0 ? (
          <Empty description="No favorite teams added yet." />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={configuredTeams}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveTeam(item.teamId)}
                  />,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    item.teamBadge ? (
                      <Image
                        src={item.teamBadge}
                        alt={item.teamName}
                        style={{ width: 32, height: 32, objectFit: "contain" }}
                        preview={false}
                      />
                    ) : null
                  }
                  title={item.teamName}
                  description={`${item.sport} - ${item.leagueName}`}
                />
              </List.Item>
            )}
          />
        )}
      </Flex>
    </Modal>
  );
}

export function DashboardYourTeam() {
  const { token, screens } = useToken();
  const [favoriteTeams, setFavoriteTeams] = useState<TFavoriteTeam[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [events, setEvents] = useState<Record<string, TEvent[]>>({});
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [expandedTeams, setExpandedTeams] = useState<Record<string, boolean>>(
    {}
  );
  const [eventToAddToTimeline, setEventToAddToTimeline] =
    useState<TEvent | null>(null);

  // ... (hooks e handlers inalterados: useEffect load favorites, handleSaveFavorites, useEffect fetch events, handleToggleExpand) ...
  useEffect(() => {
    const savedFavorites = localStorage.getItem("userFavoriteTeams");
    if (savedFavorites) {
      try {
        setFavoriteTeams(JSON.parse(savedFavorites));
      } catch (e) {
        console.error("Failed to parse saved favorites:", e);
        localStorage.removeItem("userFavoriteTeams");
      }
    }
  }, []);

  const handleSaveFavorites = useCallback((teams: TFavoriteTeam[]) => {
    setFavoriteTeams(teams);
    localStorage.setItem("userFavoriteTeams", JSON.stringify(teams));
    message.success("Favorite teams updated!");
    setExpandedTeams({});
  }, []);

  useEffect(() => {
    if (favoriteTeams.length === 0) {
      setEvents({});
      return;
    }
    setLoadingEvents(true);
    setEvents({});
    const fetchPromises = favoriteTeams.map((team) =>
      fetchData<{ results: TEvent[] | null }>(
        `${API_BASE_URL}/${API_KEY}/eventslast.php?id=${team.teamId}`
      )
        .then((data) => ({ teamId: team.teamId, events: data?.results ?? [] }))
        .catch((error) => {
          console.error(
            `Error fetching events for team ${team.teamId}:`,
            error
          );
          return { teamId: team.teamId, events: [] };
        })
    );
    Promise.all(fetchPromises)
      .then((results) => {
        const eventsByTeam: Record<string, TEvent[]> = {};
        results.forEach((result) => {
          if (result) {
            eventsByTeam[result.teamId] = result.events;
          }
        });
        setEvents(eventsByTeam);
      })
      .finally(() => {
        setLoadingEvents(false);
      });
  }, [favoriteTeams]);

  const handleToggleExpand = (teamId: string) => {
    setExpandedTeams((prev) => ({
      ...prev,
      [teamId]: !prev[teamId],
    }));
  };

  const renderEvent = (event: TEvent) => {
    // ... (lógica renderEvent inalterada, incluindo botão "Add to Timeline") ...
    const badgeStyle: React.CSSProperties = {
      width: 40,
      height: 40,
      objectFit: "contain",
    };
    const teamNameStyle: React.CSSProperties = {
      fontSize: "0.85em",
      textAlign: "center",
      marginTop: "4px",
      display: "block",
    };
    const scoreStyle: React.CSSProperties = {
      fontSize: "2.6em",
      fontWeight: "bold",
      margin: "0 10px",
    };
    const scoreDisplay =
      event.intHomeScore !== null && event.intAwayScore !== null
        ? `${event.intHomeScore} - ${event.intAwayScore}`
        : "vs";
    const dateStr =
      event.dateEvent && event.strTime
        ? `${event.dateEvent} ${event.strTime}`
        : event.dateEvent || "Date TBD";

    return (
      <List.Item key={event.idEvent} style={{ padding: "12px 0" }}>
        <Flex vertical align="center" style={{ width: "100%" }}>
          <Flex
            justify="center"
            align="center"
            style={{ width: "100%", marginBottom: 8 }}
          >
            <Flex vertical align="center" style={{ flex: 1 }}>
              {event.strHomeTeamBadge ? (
                <Image
                  src={event.strHomeTeamBadge}
                  alt={event.strHomeTeam}
                  style={badgeStyle}
                  preview={false}
                />
              ) : (
                <div style={badgeStyle} />
              )}
              <Typography.Text style={teamNameStyle}>
                {event.strHomeTeam}
              </Typography.Text>
            </Flex>
            <Typography.Text style={scoreStyle}>{scoreDisplay}</Typography.Text>
            <Flex vertical align="center" style={{ flex: 1 }}>
              {event.strAwayTeamBadge ? (
                <Image
                  src={event.strAwayTeamBadge}
                  alt={event.strAwayTeam}
                  style={badgeStyle}
                  preview={false}
                />
              ) : (
                <div style={badgeStyle} />
              )}
              <Typography.Text style={teamNameStyle}>
                {event.strAwayTeam}
              </Typography.Text>
            </Flex>
          </Flex>
          <Flex vertical align="center" style={{ width: "100%", marginTop: 8 }}>
            <Flex vertical>
              <Typography.Text
                type="secondary"
                style={{ fontSize: "0.9em", textAlign: "center" }}
              >
                {dateStr}
              </Typography.Text>
              <Typography.Text
                type="secondary"
                style={{ fontSize: "0.9em", textAlign: "center" }}
              >
                {event.strLeague || event.strEvent}
              </Typography.Text>
            </Flex>

            <Flex
              align="center"
              justify="end"
              style={{
                width: "100%",
                marginTop: token.marginSM,
              }}
            >
              <Button
                type="primary"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setEventToAddToTimeline(event);
                }}
              >
                Add to Timeline
              </Button>
            </Flex>

            {event.strStatus && event.strStatus !== "Match Finished" && (
              <Tag style={{ marginTop: 4 }}>{event.strStatus}</Tag>
            )}
          </Flex>
        </Flex>
      </List.Item>
    );
  };

  return (
    <Flex
      vertical
      gap="middle"
      style={{
        width: "100%",
        borderRadius: token.borderRadiusLG,
        border: `1px solid ${token.colorBorderSecondary}`,
        backdropFilter: "blur(20px)",
        padding: token.paddingMD,
      }}
    >
      {eventToAddToTimeline && (
        <ModalPostNew
          suggest={{
            date: eventToAddToTimeline.dateEvent
              ? dayjs(eventToAddToTimeline.dateEvent).format("YYYY-MM-DD")
              : dayjs().format("YYYY-MM-DD"),

            title: `${eventToAddToTimeline.strHomeTeam} vs ${eventToAddToTimeline.strAwayTeam}`,
            location: eventToAddToTimeline.strLeague || "",
            text: eventToAddToTimeline
              ? `Event: ${eventToAddToTimeline.strEvent}\nLeague: ${
                  eventToAddToTimeline.strLeague
                }\nDate: ${eventToAddToTimeline.dateEvent}${
                  eventToAddToTimeline.strTime
                    ? ` ${eventToAddToTimeline.strTime}`
                    : ""
                }\nScore: ${
                  eventToAddToTimeline.intHomeScore !== null
                    ? `${eventToAddToTimeline.intHomeScore}-${eventToAddToTimeline.intAwayScore}`
                    : "Not available"
                }`
              : "",
          }}
          initialEventData={eventToAddToTimeline}
          onClose={() => setEventToAddToTimeline(null)}
          onCreate={(newPost) => {
            message.success(`Event "${newPost.title}" added to timeline!`);
            setEventToAddToTimeline(null);
          }}
        />
      )}
      <Flex justify="space-between" align="center">
        <Typography.Title level={4} style={{ margin: 0 }}>
          Sports
        </Typography.Title>
        <Button icon={<SettingOutlined />} onClick={() => setIsModalOpen(true)}>
          Add new Sports
        </Button>
      </Flex>

      {loadingEvents && (
        <Spin tip="Loading events..." size="large">
          <div style={{ padding: 50 }} />
        </Spin>
      )}
      {!loadingEvents && favoriteTeams.length === 0 && (
        <Empty description="No favorite teams configured." />
      )}
      {!loadingEvents &&
        favoriteTeams.length > 0 &&
        !Object.values(events).some((list) => list.length > 0) && (
          <Empty description="No recent events found for your teams." />
        )}

      {!loadingEvents &&
        favoriteTeams.length > 0 &&
        Object.values(events).some((list) => list.length > 0) && (
          <Flex vertical gap="large">
            {favoriteTeams.map((team) => {
              const allTeamEvents = events[team.teamId] || [];
              const isExpanded = !!expandedTeams[team.teamId];
              const eventsToShow = isExpanded
                ? allTeamEvents
                : allTeamEvents.slice(0, 1);

              return allTeamEvents.length > 0 ? (
                <Card
                  key={team.teamId}
                  size="small"
                  title={
                    <Flex align="center" justify="space-between">
                      <Flex align="center" gap="small">
                        {team.teamBadge && (
                          <Image
                            src={team.teamBadge}
                            alt=""
                            style={{
                              width: 20,
                              height: 20,
                              objectFit: "contain",
                            }}
                            preview={false}
                          />
                        )}
                        <Typography.Text strong>
                          {team.teamName}
                        </Typography.Text>
                        <Typography.Text type="secondary">
                          {" "}
                          - Recent Events
                        </Typography.Text>
                      </Flex>

                      {allTeamEvents.length > 2 && (
                        <Flex align="center" justify="center">
                          <Button
                            type="link"
                            icon={
                              isExpanded ? (
                                <TbArrowsDiagonalMinimize2 />
                              ) : (
                                <ExpandOutlined />
                              )
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleExpand(team.teamId);
                            }}
                          >
                            {isExpanded ? "View Less" : "View More"}
                          </Button>
                        </Flex>
                      )}
                    </Flex>
                  }
                  style={{ backgroundColor: "transparent" }}
                >
                  <List
                    size="small"
                    itemLayout="vertical"
                    dataSource={eventsToShow}
                    renderItem={(event) => renderEvent(event)}
                  />
                </Card>
              ) : null;
            })}
          </Flex>
        )}

      {isModalOpen && (
        <SettingsModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          initialTeams={favoriteTeams}
          onSave={handleSaveFavorites}
        />
      )}
    </Flex>
  );
}
